/**
 * Uma campanha por dentro: cabeçalho com status/verba/datas, KPIs do período
 * e os conjuntos com seus anúncios. Rota `/ads/campaigns/:id`.
 */

import { useState } from "react";
import { Link, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Clock, Eye, Loader2, MousePointerClick, Pencil, Target, Wallet } from "lucide-react";
import { AdsKpi } from "@/components/ads/AdsKpi";
import { AdsetsTable } from "@/components/ads/AdsetsTable";
import { StatusToggle } from "@/components/ads/StatusToggle";
import { BudgetDialog } from "@/components/ads/BudgetDialog";
import { RowActionsMenu } from "@/components/ads/RowActionsMenu";
import { PERIODOS, moeda, numero, percentual, rotuloResultado, statusDe, OBJETIVO, type Periodo } from "@/lib/meta-ads-format";

function dataCurta(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("pt-BR");
}

function AdsCampaignContent({ campaignId }: { campaignId: string }) {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const canManage = can("ads:manage");
  const canActivate = can("ads:activate");
  const [periodo, setPeriodo] = useState<Periodo>("last_7d");
  const [editandoVerba, setEditandoVerba] = useState(false);

  const campanha = trpc.metaAds.campaigns.get.useQuery({ campaignId }, { staleTime: 30_000, retry: false });
  const contas = trpc.metaAds.accounts.list.useQuery({ includeAll: true }, { staleTime: 60_000 });
  const conta = contas.data?.find((a) => a.accountId === campanha.data?.accountId) ?? null;
  const currency = campanha.data?.currency ?? conta?.currency ?? null;
  const contaBloqueada = conta?.accountStatus != null && ![1, 9].includes(conta.accountStatus);

  const resumo = trpc.metaAds.insights.get.useQuery(
    { level: "campaign", objectId: campaignId, datePreset: periodo, limit: 1 },
    { staleTime: 60_000, enabled: !!campanha.data },
  );
  const total = resumo.data?.items?.[0];
  const invalidar = () => void utils.metaAds.campaigns.get.invalidate({ campaignId });

  if (campanha.isLoading) {
    return (
      <p className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando campanha…
      </p>
    );
  }
  if (campanha.isError || !campanha.data) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-red-600">{campanha.error ? toUserFriendlyError(campanha.error) : "Campanha não encontrada nesta conta."}</p>
        <Link href="/ads" className="mt-4 inline-block text-sm text-primary hover:underline">Voltar para Anúncios</Link>
      </div>
    );
  }

  const c = campanha.data;
  const st = statusDe(c.effectiveStatus);
  const usaVerbaTotal = c.dailyBudgetCents == null && c.lifetimeBudgetCents != null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto min-w-0">
          <Link href="/ads" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
            <ArrowLeft className="h-3 w-3" /> Anúncios
          </Link>
          <h1 className="mt-1 text-xl font-semibold leading-tight">{c.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            {canActivate && !contaBloqueada && (
              <StatusToggle objectId={c.id} objectType="campaign" status={c.status} effectiveStatus={c.effectiveStatus} dailyBudgetCents={c.dailyBudgetCents} currency={currency} onChanged={invalidar} />
            )}
            <Badge className={st.className}>{st.label}</Badge>
            <Badge variant="outline">{OBJETIVO[c.objective ?? ""] ?? c.objective ?? "—"}</Badge>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" />
              {c.dailyBudgetCents != null
                ? `${moeda(c.dailyBudgetCents, currency)}/dia`
                : c.lifetimeBudgetCents != null
                  ? `${moeda(c.lifetimeBudgetCents, currency)} total`
                  : "verba nos conjuntos"}
              {canActivate && !contaBloqueada && (c.dailyBudgetCents != null || c.lifetimeBudgetCents != null) && (
                <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => setEditandoVerba(true)} title="Editar verba">
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </span>
            {(c.startTime || c.stopTime) && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {dataCurta(c.startTime) ?? "—"} → {dataCurta(c.stopTime) ?? "sem fim"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodo} onValueChange={(v) => setPeriodo(v as Periodo)}>
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODOS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RowActionsMenu
            objectType="campaign"
            objectId={c.id}
            nome={c.name}
            adAccountId={c.accountId}
            canManage={canManage}
            canActivate={canActivate}
            contaBloqueada={contaBloqueada}
            onEditBudget={() => setEditandoVerba(true)}
            onDuplicated={() => void utils.metaAds.campaigns.list.invalidate()}
          />
        </div>
      </div>

      {contaBloqueada && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          A conta de anúncios desta campanha não está ativa na Meta. Alterações vão ser recusadas até a pendência ser resolvida no Gerenciador de Negócios.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdsKpi icon={<Wallet className="h-4 w-4" />} label="Gasto" value={moeda(total?.spendCents, currency)} hint={c.budgetRemainingCents != null ? `Restante: ${moeda(c.budgetRemainingCents, currency)}` : undefined} />
        <AdsKpi icon={<Eye className="h-4 w-4" />} label="Impressões" value={numero(total?.impressions)} hint={total?.reach ? `${numero(total.reach)} pessoas alcançadas` : undefined} />
        <AdsKpi icon={<MousePointerClick className="h-4 w-4" />} label="Cliques" value={numero(total?.clicks)} hint={total?.ctr != null ? `CTR ${percentual(total.ctr)}` : undefined} />
        <AdsKpi
          icon={<Target className="h-4 w-4" />}
          label={total?.resultado ? `Resultados (${rotuloResultado(total.resultado.type)})` : "Resultados"}
          value={numero(total?.resultado?.value)}
          hint={total?.cpcCents != null ? `CPC ${moeda(total.cpcCents, currency)}` : undefined}
        />
      </div>

      {conta?.timezoneName && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" /> Os períodos seguem o fuso da conta de anúncios ({conta.timezoneName}).
        </p>
      )}

      <AdsetsTable campaignId={c.id} adAccountId={c.accountId} currency={currency} periodo={periodo} canManage={canManage} canActivate={canActivate} contaBloqueada={contaBloqueada} />

      <BudgetDialog
        open={editandoVerba}
        onOpenChange={setEditandoVerba}
        objectType="campaign"
        objectId={c.id}
        nome={c.name}
        currentDailyCents={c.dailyBudgetCents}
        usaVerbaTotal={usaVerbaTotal}
        currency={currency}
        onSaved={invalidar}
      />
    </div>
  );
}

export default function AdsCampaign() {
  const params = useParams<{ id: string }>();
  return (
    <PermissionGuard permission="ads:view">
      <DashboardLayout>
        <AdsCampaignContent key={params.id} campaignId={params.id} />
      </DashboardLayout>
    </PermissionGuard>
  );
}
